import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Loader2, PlusCircle, Music2, Clock, Hash } from "lucide-react";
import { api } from "@/lib/api";
import { t } from "@/lib/i18n";
import { VIDEOS } from "@/constants/testIds";

const NONE = "__none__";

export default function VideoIdeas({ open, onOpenChange, accounts, onDraftCreated }) {
  const [accountId, setAccountId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ideas, setIdeas] = useState([]);
  const [creating, setCreating] = useState(null);

  const generate = async () => {
    setBusy(true);
    try {
      const res = await api.aiVideoIdeas({
        account_id: accountId || undefined,
        count: 3,
      });
      setIdeas(Array.isArray(res?.ideas) ? res.ideas : []);
    } catch {
      toast.error(t.videos.ideas.failed);
    } finally {
      setBusy(false);
    }
  };

  const pickIdea = async (idea, idx) => {
    setCreating(idx);
    try {
      const filename = `idea_${Date.now()}.mp4`;
      await api.createVideo({
        filename,
        size_mb: 0,
        duration_sec: Number(idea.duration_sec) || 20,
        caption: idea.caption || idea.hook || "",
        hashtags: Array.isArray(idea.hashtags) ? idea.hashtags : [],
        trending_sound: idea.sound_style || "",
        assigned_account_id: accountId || null,
        status: "draft",
        thumbnail_url: "https://images.unsplash.com/photo-1621761191319-c6fb62004040?crop=entropy&cs=srgb&fm=jpg&w=600&q=80",
      });
      toast.success(t.videos.ideas.used);
      if (typeof onDraftCreated === "function") onDraftCreated();
    } catch {
      toast.error(t.common.saveFailed);
    } finally {
      setCreating(null);
    }
  };

  const reset = () => { setIdeas([]); setAccountId(null); setBusy(false); setCreating(null); };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="rounded-2xl max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-emerald-500" /> {t.videos.ideas.title}
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-slate-500 -mt-1">{t.videos.ideas.description}</p>

        <div className="mt-3 flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <Label className="text-xs uppercase tracking-widest text-slate-500">{t.videos.ideas.account}</Label>
            <Select
              value={accountId || NONE}
              onValueChange={(v) => setAccountId(v === NONE ? null : v)}
              disabled={busy}
            >
              <SelectTrigger data-testid={VIDEOS.ideasAccount} className="mt-1">
                <SelectValue placeholder={t.videos.ideas.accountAny} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>{t.videos.ideas.accountAny}</SelectItem>
                {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.username}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <button
            data-testid={VIDEOS.ideasGenerate}
            onClick={generate}
            disabled={busy}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium disabled:opacity-60 transition-colors"
          >
            {busy ? (
              <><Loader2 className="h-4 w-4 animate-spin" /> {t.videos.ideas.generating}</>
            ) : (
              <><Sparkles className="h-4 w-4" /> {t.videos.ideas.generate}</>
            )}
          </button>
        </div>

        <div className="mt-4 space-y-3">
          {ideas.length === 0 && !busy && (
            <div className="text-center text-sm text-slate-400 py-8">{t.videos.ideas.empty}</div>
          )}
          {ideas.map((idea, i) => (
            <div
              key={i}
              data-testid={VIDEOS.ideasCard(i)}
              className="rounded-2xl border border-slate-100 bg-white p-4 hover:border-emerald-200 transition-colors"
            >
              <div className="text-[10px] uppercase tracking-widest text-emerald-600 font-semibold">
                {t.videos.ideas.hook}
              </div>
              <div className="mt-0.5 text-slate-900 font-medium leading-snug">
                {idea.hook || "—"}
              </div>
              {idea.caption && (
                <div className="mt-2 text-sm text-slate-600 line-clamp-2">
                  {idea.caption}
                </div>
              )}
              <div className="flex flex-wrap gap-1.5 mt-2">
                {(idea.hashtags || []).slice(0, 6).map((h) => (
                  <span key={h} className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
                    #{h}
                  </span>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-slate-500">
                <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {t.videos.ideas.seconds(idea.duration_sec || 20)}</span>
                {idea.sound_style && (
                  <span className="inline-flex items-center gap-1"><Music2 className="h-3 w-3" /> {idea.sound_style}</span>
                )}
                <span className="inline-flex items-center gap-1"><Hash className="h-3 w-3" /> {(idea.hashtags || []).length}</span>
              </div>
              <div className="mt-3 pt-3 border-t border-slate-100 flex justify-end">
                <button
                  data-testid={VIDEOS.ideasUseBtn(i)}
                  onClick={() => pickIdea(idea, i)}
                  disabled={creating !== null}
                  className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white disabled:opacity-50 transition-colors"
                >
                  {creating === i ? (
                    <><Loader2 className="h-3 w-3 animate-spin" /> …</>
                  ) : (
                    <><PlusCircle className="h-3 w-3" /> {t.videos.ideas.use}</>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>

        <DialogFooter>
          <button
            data-testid={VIDEOS.ideasClose}
            onClick={() => onOpenChange(false)}
            className="px-4 py-2 rounded-xl bg-white border border-slate-200 hover:border-slate-300 text-slate-700 text-sm font-medium transition-colors"
          >
            {t.videos.ideas.close}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
